import React from "react";

function GlobalLeader() {
  return (
    <section className="w-full bg-slate-50/50 py-20 px-6 sm:px-12 relative overflow-hidden">
      {/* Background Decorative Gradient Orbs */}
      <div className="absolute top-1/4 -left-40 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto flex flex-col gap-20 relative z-10">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-orange-100 text-orange-700">
            Corporate Strategy
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
            Our Purpose & Path Forward
          </h2>
          <p className="text-slate-600 text-sm sm:text-base">
            Guided by standard-setting engineering, environmental responsibility, and structural excellence.
          </p>
        </div>

        {/* Vision Statement Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">

          {/* Vision Content Card */}
          <div data-aos="fade-right" className="lg:col-span-7 bg-white rounded-3xl p-8 sm:p-10 border border-slate-200/80 shadow-xl shadow-slate-200/50 relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-orange-500 to-amber-500"></div>

            <div className="flex flex-col space-y-6">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-orange-50 text-orange-600 rounded-xl font-bold border border-orange-100 shadow-sm">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <span className="text-xs font-bold tracking-widest text-orange-600 uppercase">
                  Our Vision
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-snug">
                Pioneering India's Fluid Management Infrastructure
              </h3>

              <p className="text-slate-700 leading-relaxed text-base sm:text-lg font-medium">
                To emerge as one of India's most respected manufacturers of polymer piping and water management solutions by delivering certified engineering, continuous innovation, and exceptional customer value.
              </p>

              <div className="space-y-4 pt-6 border-t border-slate-100 text-slate-600 text-sm sm:text-base leading-relaxed">
                <p className="italic text-slate-700 bg-orange-50/50 p-4 rounded-2xl border border-orange-100/60">
                  "At Polo Pipes, we envision building a future where our assets support the nation's infrastructure, agriculture, and urban networks through reliable, long-term solutions."
                </p>

                <p>
                  Our commitment extends to upholding rigorous manufacturing compliance, environmental responsibility, and fostering dependable relationships across our distribution channels.
                </p>

                <div className="p-4 bg-slate-900 text-white rounded-2xl flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between shadow-md">
                  <div>
                    <span className="text-xs font-semibold text-orange-400 uppercase tracking-wider block">Expansion Roadmap</span>
                    <p className="text-xs text-slate-300 mt-0.5">Integrating Water Storage Tanks, CPVC Pipes & Fittings, and  uPVC & SWR Fittings.</p>
                  </div>
                  <span className="text-[11px] font-bold px-3 py-1 bg-orange-500 text-white rounded-full whitespace-nowrap">
                    Active Growth
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Vision Visual Accent */}
          <div data-aos="fade-left" className="lg:col-span-5 flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square rounded-3xl bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 p-8 text-white shadow-2xl shadow-orange-500/20 flex flex-col justify-between overflow-hidden group">
              <div className="absolute -right-10 -bottom-10 w-60 h-60 bg-white/10 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-700"></div>

              <div className="flex justify-between items-start">
                <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <span className="text-xs font-bold tracking-widest text-orange-100 uppercase bg-white/10 px-3 py-1 rounded-full backdrop-blur-md border border-white/10">
                  Benchmark Quality
                </span>
              </div>

              <div className="space-y-2 relative z-10">
                <span className="text-4xl font-black tracking-tight">01.</span>
                <h4 className="text-2xl font-bold">Forward Focus</h4>
                <p className="text-orange-100 text-sm leading-relaxed">
                  Engineered fluid distribution infrastructure designed for endurance and high performance.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mission Statement Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">

          {/* Mission Visual Accent */}
          <div data-aos="fade-right" className="lg:col-span-5 order-2 lg:order-1 flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 p-8 text-white shadow-2xl shadow-slate-900/30 flex flex-col justify-between overflow-hidden group">
              <div className="absolute -left-10 -top-10 w-60 h-60 bg-blue-500/10 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-700"></div>

              <div className="flex justify-between items-start">
                <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                  <svg className="w-8 h-8 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="text-xs font-bold tracking-widest text-sky-300 uppercase bg-white/10 px-3 py-1 rounded-full backdrop-blur-md border border-white/10">
                  Core Values
                </span>
              </div>

              <div className="space-y-2 relative z-10">
                <span className="text-4xl font-black tracking-tight text-sky-400">02.</span>
                <h4 className="text-2xl font-bold">Mission Driven</h4>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Executing rigorous standard testing, technical transparency, and continuous supply reliability.
                </p>
              </div>
            </div>
          </div>

          {/* Mission Content Card */}
          <div data-aos="fade-left" className="lg:col-span-7 order-1 lg:order-2 bg-white rounded-3xl p-8 sm:p-10 border border-slate-200/80 shadow-xl shadow-slate-200/50 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-sky-500 to-blue-600"></div>

            <div className="flex flex-col space-y-6">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-sky-50 text-sky-600 rounded-xl font-bold border border-sky-100 shadow-sm">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <span className="text-xs font-bold tracking-widest text-sky-600 uppercase">
                  Our Mission
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-snug">
                Delivering Quality Through Engineering Integrity
              </h3>

              {/* Mission Grid List */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-sky-50/50 hover:border-sky-100 transition-all duration-300">
                  <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center font-bold text-sm mb-3">
                    01
                  </div>
                  <h5 className="font-bold text-slate-900 text-sm mb-1">Precision Manufacturing</h5>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    Producing piping systems that consistently exceed standard benchmark regulations.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-sky-50/50 hover:border-sky-100 transition-all duration-300">
                  <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center font-bold text-sm mb-3">
                    02
                  </div>
                  <h5 className="font-bold text-slate-900 text-sm mb-1">Product Innovation</h5>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    Delivering cost-efficient fluid solutions through modernized compounding techniques.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-sky-50/50 hover:border-sky-100 transition-all duration-300">
                  <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center font-bold text-sm mb-3">
                    03
                  </div>
                  <h5 className="font-bold text-slate-900 text-sm mb-1">Distribution Footprint</h5>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    Expanding institutional network accessibility across key regional markets.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-sky-50/50 hover:border-sky-100 transition-all duration-300">
                  <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center font-bold text-sm mb-3">
                    04
                  </div>
                  <h5 className="font-bold text-slate-900 text-sm mb-1">Stakeholder Trust</h5>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    Fostering long-term partner relationships with transparent communication and delivery.
                  </p>
                </div>

              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}

export default GlobalLeader;